import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { object } from 'yup';
import { Alert, Modal, Button, Row, Col, Icon } from '@storable/canaveral-components';
import FormFieldPageSelect from '../../containers/FormFieldPageSelect';
import withForm from '../../hocs/withForm';
import { FACILITY_PAGE, PAGE_TYPES } from '../../../data/db/lib/typeCodes';
import styles from './ApplyPageLayoutModal.scss';

const excludeTypes = PAGE_TYPES.filter(type => type !== FACILITY_PAGE);

const validationSchema = object().shape({
  destinationPage: object().required('Required'),
  sourcePage: object().required('Required'),
});

const propTypes = {
  isValid: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    metadata: PropTypes.shape({
      title: PropTypes.string,
      internalPageName: PropTypes.string,
    }),
  }).isRequired,
  submitErrors: PropTypes.arrayOf(PropTypes.string),
  values: PropTypes.object.isRequired,
};

const defaultProps = {
  loading: undefined,
  submitErrors: [],
};

const confirmAlert = (
  <Alert context="warning" className={styles.alert}>
    <Row>
      <Col xs={1}>
        <Icon id="warning-triangle" className={styles.icon} label="warning" />
      </Col>
      <Col xs={11}>
        <div>
          <strong>
            Please confirm that you wish to apply the following layout changes.
          </strong>
        </div>
        Applying a page layout will replace all content, sections and widgets
        on this page with the most recently published version from the selected source page.
      </Col>
    </Row>
  </Alert>
);

const successAlert = (
  <Alert context="success" className={styles.alert}>
    <Col xs={1}>
      <Icon id="success" className={styles.icon} label="success" />
    </Col>
    <Col xs={11}>
      Your page layout has been successfully applied!
    </Col>
  </Alert>
);

const ApplyPageLayoutModal = ({
  onClose, page, isValid, values, onConfirm, loading, submitErrors,
}) => {
  const [submitted, setSubmitted] = useState(false);

  const steps = {
    select: {
      name: 'select',
      cancelText: 'Cancel',
      cancelFn: onClose,
      nextText: 'Next',
      // eslint-disable-next-line no-use-before-define
      nextFn: () => setStep(steps.confirm),
      nextDisabled: !isValid,
      closeFn: onClose,
    },
    confirm: {
      name: 'confirm',
      cancelText: 'Back',
      // eslint-disable-next-line no-use-before-define
      cancelFn: () => setStep(steps.select),
      nextText: 'Confirm',
      nextFn: () => {
        setSubmitted(true);
        onConfirm({
          destinationPageId: values.destinationPage.id, sourcePageId: values.sourcePage.id,
        });
      },
      nextDisabled: loading,
      selectDisabled: true,
      alert: confirmAlert,
      showErrors: true,
      closeFn: onClose,
    },
    success: {
      name: 'success',
      alert: successAlert,
      hideSelects: true,
      cancelFn: () => window.location.reload(),
      closeFn: () => window.location.reload(),
      cancelText: 'Close',
    },
  };

  const [step, setStep] = useState(steps.select);

  useEffect(() => {
    setStep(steps[step.name]);
  }, [isValid]);

  useEffect(() => {
    if (submitted && !loading && !submitErrors.length) {
      setStep(steps.success);
    }
  }, [loading, submitErrors]);

  return (
    <Modal
      show
      onDismiss={step.closeFn}
    >
      <Modal.Header>
        <Modal.Title id="apply-page-layout">
          Apply Page Layout
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step.alert}
        {step.showErrors && !!submitErrors.length && submitErrors.map(err => (
          <Alert key={err} context="danger">
            {err}
          </Alert>
        ))}
        {!step.hideSelects && (
          <>
            <FormFieldPageSelect
              currentPageId={page.id}
              disabled={step.selectDisabled}
              excludeTypes={excludeTypes}
              includeDate
              label="Apply Page Layout From"
              name="sourcePage"
              showDrafts
            />
            <div className={styles.label}>
              Apply Page Layout To
            </div>
            <div className={styles.destination}>
              {page.metadata.internalPageName || page.metadata.title}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={step.cancelFn} context="neutral">
          {step.cancelText}
        </Button>
        {step.nextFn && (
          <Button onClick={step.nextFn} context="positive" disabled={step.nextDisabled}>
            {step.nextText}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
ApplyPageLayoutModal.propTypes = propTypes;
ApplyPageLayoutModal.defaultProps = defaultProps;

export default withForm({
  mapPropsToValues: props => ({
    destinationPage: props.page,
  }),
  validationSchema,
  handleSubmit: (updatedFacility, formikBag) => formikBag.props.onSubmit(updatedFacility),
})(ApplyPageLayoutModal);
